import { slugify as sharedSlugify, uniquify } from '@/lib/slug';

import { UNASSIGNED_PHASE_ID } from './types';
import type { DraftPhase, DraftStep, WorkflowDraft } from './types';

/**
 * Identifier derivation for the builder (research D15).
 *
 * Ids are what tasks reference and what transitions target, so they are
 * load-bearing — but nobody wants to type one. The rule that squares this: an
 * id follows its name until someone takes it over, and once taken over it stops
 * following. `idPinned` records which side of that line an entity is on.
 *
 * A published workflow pins everything on load. Its ids are already referenced
 * by real tasks, and quietly rewriting one because a user tidied a display name
 * is how you orphan work.
 */

/**
 * A name turned into an id: lowercase, non-alphanumerics collapsed to single
 * hyphens, trimmed.
 *
 * Falls back to `step` for a name that slugifies to nothing — an id is required
 * and "" is not one. A name of only punctuation is unusual but not an error the
 * user should be stopped for.
 */
export function slugify(name: string): string {
  return sharedSlugify(name, 'step');
}

/**
 * `slugify`, then uniquified against `taken` by appending `-2`, `-3`, … (FR-033).
 */
export function deriveId(name: string, taken: Iterable<string>): string {
  return uniquify(slugify(name), taken);
}

/**
 * Whether this entity's id should still track its name (FR-034).
 *
 * Two conditions, and both matter: a pinned id has been chosen deliberately,
 * and a published workflow's ids are referenced by tasks. Either one stops the
 * following.
 */
export function shouldFollow(
  entity: { idPinned: boolean },
  status: WorkflowDraft['status'],
): boolean {
  return !entity.idPinned && status === 'draft';
}

/**
 * Whether a *loaded* identifier should be treated as pinned (D15).
 *
 * Published: always, as above. Draft: only when the id differs from what its
 * name would derive — which is the evidence that someone set it by hand. This
 * is a heuristic, and deliberately the conservative one: mistaking a pinned id
 * for a following one silently rewrites it, while the reverse merely means a
 * user has to edit an id they could have had for free.
 */
export function pinnedOnLoad(
  name: string,
  id: string,
  status: WorkflowDraft['status'],
): boolean {
  if (status === 'published') return true;
  return id !== slugify(name);
}

/** Every id in the draft that a new step or phase would have to avoid. */
export function takenIds(draft: WorkflowDraft): Set<string> {
  const taken = new Set<string>();
  for (const phase of draft.phases) {
    taken.add(phase.id);
    for (const step of phase.steps) taken.add(step.id);
  }
  return taken;
}

export type IdentifiedEntity =
  { kind: 'step'; step: DraftStep } | { kind: 'phase'; phase: DraftPhase };

/**
 * The entity already using `id`, or null (FR-037).
 *
 * `self` excludes the entity being renamed, so re-committing an unchanged id is
 * not reported as colliding with itself. Steps and phases share one namespace
 * because the service's reserved-prefix rule and the layout's node keys both
 * treat them as one.
 */
export function idCollision(
  draft: WorkflowDraft,
  id: string,
  self?: string,
): IdentifiedEntity | null {
  for (const phase of draft.phases) {
    if (phase.id === id && phase.id !== self) return { kind: 'phase', phase };
    for (const step of phase.steps) {
      if (step.id === id && step.id !== self) return { kind: 'step', step };
    }
  }
  return null;
}

/**
 * Rewrite every reference to `oldId` after an id changes (FR-035).
 *
 * Three places reference a step id and all three have to move together, or the
 * rename silently breaks the graph: transition targets, the declared start, and
 * the layout key. Returned as a new draft so the caller can make it one reducer
 * action, and therefore one undo entry.
 */
export function renameCascade(
  draft: WorkflowDraft,
  oldId: string,
  newId: string,
): WorkflowDraft {
  if (oldId === newId) return draft;

  const phases = draft.phases.map((phase) => ({
    ...phase,
    steps: phase.steps.map((step) => ({
      ...step,
      id: step.id === oldId ? newId : step.id,
      transitions: step.transitions.map((transition) =>
        transition.to === oldId ? { ...transition, to: newId } : transition,
      ),
    })),
  }));

  const layout = { ...draft.layout };
  if (oldId in layout) {
    layout[newId] = layout[oldId];
    delete layout[oldId];
  }

  return {
    ...draft,
    phases,
    layout,
    start: draft.start === oldId ? newId : draft.start,
  };
}

/**
 * Whether an id may be used at all.
 *
 * The `$` namespace is the service's, and `$unassigned` is the one id in it a
 * client may hold — but only because the builder puts it there, never because a
 * user typed it.
 */
export function isReservedId(id: string): boolean {
  return id.startsWith('$') && id !== UNASSIGNED_PHASE_ID;
}

/**
 * Why this identifier cannot be used, or `null` if it can (FR-037).
 *
 * Here rather than in an inspector because both inspectors need it and had
 * byte-identical copies — including the wording of every message. Two copies of
 * user-facing text is one copy too many: the next edit fixes the phrasing a user
 * sees when renaming a step and leaves the phase dialog saying something else.
 */
export function idErrorFor(
  draft: WorkflowDraft,
  id: string,
  self?: string,
): string | null {
  if (!id) return 'An identifier is required.';
  if (isReservedId(id)) {
    return 'Identifiers beginning with “$” are reserved by the system.';
  }
  const clash = idCollision(draft, id, self ?? id);
  if (clash) {
    return clash.kind === 'step'
      ? `The step “${clash.step.name}” already uses this identifier.`
      : `The phase “${clash.phase.name}” already uses this identifier.`;
  }
  return null;
}
